<template>
  <div class="fat-type-block" :style="{ background: bg }">
    <div v-if="loading" class="hint">Считаю…</div>
    <template v-else>
      <div v-if="rows.length" class="rows">
        <div v-for="row in rows" :key="row.key" class="row">
          <div class="line line--dish" :class="{ 'cursor-pointer': row.parts.length }" @click="toggleRow(row)">
            <span class="name">{{ row.name }}</span>
            <span class="value"><span v-if="row.approx" class="approx-mark">~</span>{{ fmt(row.value) }}</span>
          </div>
          <template v-if="opened.has(row.key)">
            <div v-for="part in row.parts" :key="part.key" class="line line--part">
              <span class="name">{{ part.name }}</span>
              <span class="value"><span v-if="part.approx" class="approx-mark">~</span>{{ fmt(part.value) }}</span>
            </div>
          </template>
        </div>
      </div>
      <p v-else class="hint">{{ emptyText }}</p>

      <!-- Непокрытая часть жира не превращается в молчаливый ноль. Пока идёт
           дооценка, обещание «обновится само» честное: Diary перезапрашивает
           данные, когда пачка должна была записаться. -->
      <p v-if="coverage < 95" class="hint">
        Данные есть для ~{{ coverage }}% {{ macroLabel }} дня<template v-if="estimating">. ИИ оценивает остальные продукты — цифры уточнятся сами через ~{{ estMinutes }} мин</template>
      </p>
    </template>
  </div>
</template>

<script setup>
import { computed, reactive } from 'vue'

// Раскладка блюда по умолчанию свёрнута и открывается тапом по блюду:
// разворачивать всё сразу было решено ещё для клетчатки с её короткими
// списками, но при шести раскрытиях день превращался в простыню строк.
const opened = reactive(new Set())
function toggleRow(row) {
  if (!row.parts.length) return
  if (opened.has(row.key)) opened.delete(row.key)
  else opened.add(row.key)
}


// Раскладка ОДНОГО типа жира (насыщенные или омега-3) за день: блюда и их
// ингредиенты по убыванию вклада. Значения считает сервер (GET /diary/fats,
// загружает Diary и раздаёт полоскам и этим блокам через пропсы); измеренное
// важнее ИИ-оценки, оценки помечены «~».
const props = defineProps({
  metric: { type: String, required: true },          // 'sat' | 'omega3'
  entries: { type: Array, default: () => [] },       // записи дня из GET /diary
  values: { type: Object, default: () => ({}) },     // data.entries из /diary/fats
  parts: { type: Object, default: () => ({}) },      // data.parts из /diary/fats
  loading: { type: Boolean, default: false },
  coverage: { type: Number, default: 100 },
  // Ниже порога источник не показываем: список отвечает «откуда», а не
  // перечисляет нули. У омега-3 порог и точность мельче — там и значения мельче.
  min: { type: Number, default: 0.3 },
  decimals: { type: Number, default: 1 },
  bg: { type: String, default: 'rgba(235, 192, 109, 0.2)' },
  emptyText: { type: String, default: 'Заметных источников сегодня нет' },
  // Сколько карточек сервер отправил на дооценку по этому запросу
  estimating: { type: Number, default: 0 },
  // Чего именно «дня» покрыто данными: «жиров» у типов жира, «углеводов» у сахаров
  macroLabel: { type: String, default: 'жиров' },
})

// Пачка в 40 карточек — один вызов Gemini, около минуты с запасом
const estMinutes = computed(() => Math.max(1, Math.ceil(props.estimating / 40)))


function fmt(v) {
  const k = Math.pow(10, props.decimals)
  return Math.round((v || 0) * k) / k
}

// Группировка та же, что на карточках дневника и в MacroSourcesBlock:
// записи с общим dish_id — одно блюдо, одиночная запись — сама себе блюдо.
const rows = computed(() => {
  const groups = new Map()
  for (const e of props.entries) {
    const key = e.dish_id ? `dish-${e.dish_id}` : `entry-${e.id}`
    if (!groups.has(key)) groups.set(key, { key, name: null, entries: [] })
    const g = groups.get(key)
    g.entries.push(e)
    if (e.dish_name && !g.name) g.name = e.dish_name
  }

  const out = []
  for (const g of groups.values()) {
    let total = 0
    let approx = false
    const parts = []
    for (const e of g.entries) {
      const v = props.values[e.id]
      const num = v?.[props.metric]
      if (num == null) continue
      total += num
      if (v[`${props.metric}_approx`]) approx = true
      // Части: у блюда одной записью — раскладка с сервера,
      // у блюда из нескольких записей частью служит сама запись
      const serverParts = props.parts[e.id]
      if (serverParts?.length) {
        for (const [i, p] of serverParts.entries()) {
          if (p[props.metric] != null && p[props.metric] >= props.min) {
            parts.push({ key: `e${e.id}-${i}`, name: p.name, value: p[props.metric], approx: !!p.approx })
          }
        }
      } else if ((g.entries.length > 1 || g.name) && num >= props.min) {
        // Запись сама себе ингредиент: у блюда из нескольких записей — всегда,
        // у блюда из одной — когда у блюда есть своё имя («Печенье к чаю» →
        // «Печенье Юбилейное»), как в MacroSourcesBlock. Совпадающее имя
        // отсеет проверка shown ниже.
        parts.push({ key: `e${e.id}`, name: e.product_name, value: num, approx: !!v[`${props.metric}_approx`] })
      }
    }
    if (total < props.min) continue
    parts.sort((a, b) => b.value - a.value)
    // Раскладка из одной строки с тем же названием повторила бы строку блюда
    const shown = (parts.length === 1
      && parts[0].name?.trim().toLowerCase() === (g.name || g.entries[0]?.product_name || '').trim().toLowerCase())
      ? [] : parts
    out.push({
      key: g.key,
      name: g.name || g.entries[0]?.product_name || 'Без названия',
      value: total,
      approx,
      parts: shown,
    })
  }
  return out.sort((a, b) => b.value - a.value)
})
</script>

<style scoped>
/* Фон продолжает свою полоску (цвет полоски с альфой 0.2 передаётся пропсом);
   верхняя линия — ::before в потоке, той же ширины, что разделители строк */
.fat-type-block {
  padding: 0 10px 8px;
  border-radius: 0 0 12px 12px;
  margin-bottom: 6px;
}
.fat-type-block::before {
  content: '';
  display: block;
  height: 1px;
  margin-bottom: 2px;
  background: rgba(65, 41, 26, 0.09);
}

.line {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 7px 0;
  border-bottom: 1px solid rgba(65, 41, 26, 0.09);
}
.row:last-child .line:last-child { border-bottom: none; }

.line--dish .name,
.line--dish .value {
  font-family: Inter, sans-serif;
  font-size: var(--fs-14);
  font-weight: 600;
  color: var(--ink);
  line-height: 1.25;
}
.line--dish .name { flex: 1 1 auto; min-width: 0; }
.line--dish .value { white-space: nowrap; }

.line--part { padding-left: 14px; }
.line--part .name,
.line--part .value {
  font-family: Inter, sans-serif;
  font-size: var(--fs-14);
  font-weight: 400;
  color: var(--ink);
  line-height: 1.25;
}
.line--part .name { flex: 1 1 auto; min-width: 0; }
.line--part .value { white-space: nowrap; }

/* Знак «~» — общий класс .approx-mark в style.css */

.hint {
  font-family: Inter, sans-serif;
  font-size: var(--fs-12);
  color: var(--ink-70);
  line-height: 1.35;
  margin: 0;
  padding: 6px 0;
}

</style>
